import { DateTime } from "luxon";

import DatePicker, { DatePickerVariant } from "@/components/DatePicker";
import ScheduleMessageRecurringOptions from "@/components/Scheduling/ScheduleMessageRecurringOptions";
import TimePicker from "@/components/TimePicker";
import { firstOccurrence, sundayFirstDayNumber } from "@/utils/date";

type Props = {
  type: "date" | "time" | "daysOfWeek";
  dateTime: Date;
  saving: boolean;
  setDateTime: (date: Date, dayOfWeek: number) => void;
  label?: string;
  doesRepeat?: boolean;
  setMutableDoesRepeat?: (value: boolean) => void;
  daysOfWeek?: number;
  setInvalidTime?: (value: boolean) => void;
};
export default function ScheduleDateTimePicker({
  daysOfWeek,
  saving,
  doesRepeat,
  type,
  label,
  dateTime,
  setDateTime,
  setMutableDoesRepeat,
  setInvalidTime,
}: Props) {
  let minDatetime = null;
  const isTimeInFuture = DateTime.fromJSDate(dateTime) > DateTime.now();
  if (isTimeInFuture) {
    minDatetime = DateTime.fromJSDate(dateTime).startOf("day").toJSDate();
  } else if (!doesRepeat) {
    minDatetime = dateTime;
  }

  const handleDateSelection = (newDateTime: Date) => {
    const pastTime = DateTime.fromJSDate(newDateTime) < DateTime.now();
    if (pastTime) {
      setInvalidTime(true);
    } else if (DateTime.fromJSDate(newDateTime).isValid) {
      const dayOfWeek = sundayFirstDayNumber(newDateTime);
      setDateTime(newDateTime, dayOfWeek);
      setInvalidTime(false);
    } else {
      setInvalidTime(true);
    }
  };

  const handleSelectedDaysOfWeek = (days: number[]) => {
    const targetDay = days?.[0] - 1;
    const today = DateTime.now();
    // Luxon counts Monday 1 through Sunday 7; `% 7` makes Sunday 0, as the target day counts.
    const todaysDay = today.weekday % 7;
    // Calculate the number of days to add to reach the target day
    const daysToAdd = (targetDay - todaysDay + 7) % 7;
    // The next target day by calendar, at the picked time with the seconds cleared
    const picked = DateTime.fromJSDate(dateTime);
    const nextTargetDate = picked.isValid
      ? firstOccurrence(
          today.plus({ days: daysToAdd }).set({ hour: picked.hour, minute: picked.minute, second: 0 }),
        ).toJSDate()
      : new Date(Number.NaN);
    setDateTime(nextTargetDate, days[0]);
  };

  if (type === "date") {
    return (
      <DatePicker
        label={label}
        variant={DatePickerVariant.Dropdown}
        selectedDate={dateTime}
        dateSelectionHandler={(date: Date) => handleDateSelection(date)}
        disabled={saving || doesRepeat}
      />
    );
  }

  if (type === "time") {
    return (
      <TimePicker
        label={label}
        includeTimeZone={false}
        selectedTime={dateTime}
        minDate={minDatetime}
        timeSelectionHandler={(date: Date) => handleDateSelection(date)}
        disabled={saving}
      />
    );
  }

  if (type === "daysOfWeek") {
    return (
      <ScheduleMessageRecurringOptions
        doesRepeat={doesRepeat}
        doesRepeatHandler={(value: boolean) => {
          setMutableDoesRepeat(value);
        }}
        disabled={saving}
        selectedDate={dateTime}
        selectedDaysOfWeek={[daysOfWeek]}
        selectedDaysOfWeekHandler={(days) => handleSelectedDaysOfWeek(days)}
      />
    );
  }
}
